import { useParams } from 'react-router';
import { useGetChartBESSQuery } from '@/features/bess/hooks/queries/[bess]/get-bess-charts.ts';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useMemo, useRef, useState } from 'react';
import { enUS, ro } from 'date-fns/locale';
import { cn } from '@/utils/utils.ts';
import { Button } from '@/components/ui/button.tsx';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { format, Locale } from 'date-fns';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import type { Props as LabelProps } from 'recharts/types/component/Label';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';

const METRIC_COLORS = {
  BATTERY_CHARGE: '#038BA8',
  BATTERY_DISCHARGE: '#AC44FB',
} as const;

const METRIC_KEYS = {
  BATTERY_CHARGE: 'battery_charge',
  BATTERY_DISCHARGE: 'battery_discharge',
} as const;

const CHART_METRICS = [
  {
    label: 'Battery Charge',
    color: METRIC_COLORS.BATTERY_CHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_CHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_CHARGE,
  },
  {
    label: 'Battery Discharge',
    color: METRIC_COLORS.BATTERY_DISCHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_DISCHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_DISCHARGE,
  },
];

export const BessChargeDischargePowerChart = ({ className }: { className?: string }) => {
  const { bessId } = useParams();

  const { data, isLoading, dataUpdatedAt } = useGetChartBESSQuery({ id: bessId });
  const chartData = data?.batteryChargeDischargePower || [];

  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width:640px)');
  const chartRef = useRef<HTMLDivElement>(null);
  const [fields, setFields] = useState(CHART_METRICS);

  const locale = (i18n.language as 'ro' | 'en') === 'ro' ? ro : enUS;

  const filteredFields = useMemo(() => {
    return fields.map((field) => ({
      ...field,
      label: t(`metrics.${field.id}`),
    }));
  }, [t, fields]);

  const chart = useMemo(() => {
    if (!chartData)
      return {
        isPowerOver1000: false,
        data: [],
      };

    const processValue = (value: number | null, shouldNegate = false): number | null => {
      if (value === null) return null;
      const absValue = Math.abs(value);
      return shouldNegate ? -absValue : absValue;
    };

    const isPowerOver1000 =
      chartData?.some((c) => c.battery_discharge > 1000 || c.battery_charge > 1000) || false;

    return {
      data: chartData?.map((v) => {
        const battery_charge = isPowerOver1000
          ? (processValue(v?.battery_charge) || 0) / 1000
          : processValue(v?.battery_charge);

        const battery_discharge =
          isPowerOver1000 && v?.battery_discharge
            ? (processValue(v?.battery_discharge) || 0) / 1000
            : processValue(v?.battery_discharge);

        return {
          [METRIC_KEYS.BATTERY_CHARGE]: battery_charge,
          [METRIC_KEYS.BATTERY_DISCHARGE]: battery_discharge,
          time: v?.time,
        };
      }),
      isPowerOver1000,
    };
  }, [chartData]);

  return (
    <DashboardCard
      className={cn(className, 'flex min-h-[544px] w-full flex-col gap-4 select-none')}
      isLoading={isLoading}
    >
      <div className="flex w-full flex-col">
        <h3 className="font-semibold text-nowrap">Charge/Discharge Cycles</h3>
        <LastUpdateTimestamp updatedAt={dataUpdatedAt} updateInterval={10000} />
      </div>
      {!chart?.data?.length ? (
        <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      ) : (
        <>
          <div className={cn(`relative w-full`, isMobile ? 'h-[380px]' : 'h-[420px]')}>
            <div className="absolute top-3 right-12 z-10"></div>
            <div className="h-full w-full">
              <div className="h-full" ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chart.data}
                    margin={{
                      top: 25,
                      right: isMobile ? -60 : -35,
                      bottom: 0,
                      left: isMobile ? -15 : 0,
                    }}
                  >
                    <defs>
                      {fields.map((v) => (
                        <linearGradient key={v.id} id={v.id} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={v.color} stopOpacity={1} />
                          <stop offset="95%" stopColor={v.color} stopOpacity={0.2} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid vertical={false} stroke="#e5e7eb" />
                    <XAxis
                      key="xAxis-main"
                      dataKey="time"
                      xAxisId="main"
                      minTickGap={40}
                      tickFormatter={(t) => {
                        return formatDate(t, locale);
                      }}
                      tickLine={false}
                      axisLine={false}
                      tickMargin={4}
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{
                        value: chart.isPowerOver1000 ? 'MWh' : 'kWh',
                        angle: -90,
                        position: 'left',
                        offset: isMobile ? -20 : -10,
                      }}
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{
                        value: '%',
                        angle: -90,
                        position: 'right',
                        offset: isMobile ? -30 : -25,
                        textSize: isMobile ? 12 : 16,
                      }}
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <Tooltip
                      cursor={false}
                      content={(props) => (
                        <ChartTooltipComponent
                          {...props}
                          locale={locale}
                          isPowerOver1000={chart.isPowerOver1000}
                        />
                      )}
                    />

                    {fields.map((v, index) => {
                      if (!v.selected) return null;

                      return (
                        <Bar
                          key={`bar-${v.dataKey}-${index}`}
                          xAxisId="main"
                          yAxisId={'left'}
                          dataKey={v.dataKey}
                          name={t ? t(`metrics.${v.dataKey}`) : v.label}
                          fill={`${v.color}80`}
                          stroke={v.color}
                          strokeWidth={1}
                          radius={[10, 10, 0, 0]}
                          label={(props: LabelProps) => {
                            const { x, y, value } = props;
                            if (x === undefined || y === undefined) return null;
                            let label = Number(value).toFixed(2);

                            return (
                              <g>
                                <text
                                  x={x}
                                  y={y}
                                  dy={-6}
                                  fontSize={isMobile ? 14 : 16}
                                  fill="white"
                                  stroke="white"
                                  strokeWidth={3}
                                  strokeLinejoin="round"
                                  paintOrder="stroke"
                                  textAnchor="end"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  {label}
                                </text>
                                <text
                                  x={x}
                                  y={y}
                                  dy={-6}
                                  fontSize={isMobile ? 14 : 16}
                                  fill="#7D7D85"
                                  textAnchor="end"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  {label}
                                </text>
                              </g>
                            );
                          }}
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
            {filteredFields.map((f) => {
              return (
                <Button
                  key={f.dataKey}
                  variant="ghost"
                  style={{ opacity: !f.selected ? '0.4' : '1' }}
                  className="h-min w-min justify-start p-1 px-2"
                  onClick={() => {
                    setFields((current) =>
                      current.map((c) => {
                        if (c.id === f.id)
                          return {
                            ...c,
                            selected: !c.selected,
                          };

                        return c;
                      })
                    );
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="size-4 rounded-full"
                      style={{ backgroundColor: f.color }}
                    ></span>
                    <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                      {f.label}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
        </>
      )}
    </DashboardCard>
  );
};

const ChartTooltipComponent = ({
  payload,
  label,
  locale,
  isPowerOver1000,
}: TooltipContentProps<ValueType, NameType> & {
  locale: Locale;
  isPowerOver1000: boolean;
}) => {
  const formattedDate = label ? formatDate(new Date(label), locale) : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'battery_state_of_charge') return `${p.value} %`;

    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (!isPowerOver1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = value.toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

function formatDate(date: Date, locale?: Locale): string {
  const capitalizeWords = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  return capitalizeWords(format(date, 'EEE, MMM d', { locale }));
}
